"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import axios from "axios";
import clsx from "clsx";
import { useAuthStore } from "@/store/auth";

const NAV_ITEMS = [
  { href: "/", icon: "🏠", label: "홈" },
  { href: "/manufacturers", icon: "🏭", label: "공급처" },
  { href: "/map", icon: "🗺", label: "지도" },
  { href: "/store", icon: "🏪", label: "매장" },
  { href: "/community", icon: "💬", label: "커뮤니티" },
  { href: "/mypage", icon: "👤", label: "MY" },
];

export function BottomNav() {
  const pathname = usePathname();
  const { isLoggedIn, token } = useAuthStore();
  const [mentionCount, setMentionCount] = useState(0);
  const [dmCount, setDmCount] = useState(0);
  const [friendNotiCount, setFriendNotiCount] = useState(0);
  const [supportCount, setSupportCount] = useState(0);

  // R3-33 S4-D / D-2 — 멘션 + DM + 친구 알림 통합 폴링 (1분) + #8 고객문의 답변
  useEffect(() => {
    if (!token) {
      setMentionCount(0); setDmCount(0); setFriendNotiCount(0); setSupportCount(0);
      return;
    }
    const fetchAll = async () => {
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const [m, d, f, s] = await Promise.all([
          axios.get<{ count: number }>("/api/community/mentions/unread-count", { headers }),
          axios.get<{ count: number }>("/api/dm/unread-count", { headers }),
          axios.get<{ count: number }>("/api/friends/notifications/unread-count", { headers }),
          axios.get<{ unread: number }>("/api/support/unread-count", { headers }),
        ]);
        setMentionCount(m.data.count || 0);
        setDmCount(d.data.count || 0);
        setFriendNotiCount(f.data.count || 0);
        setSupportCount(s.data.unread || 0);
      } catch {
        // noop
      }
    };
    fetchAll();
    const t = setInterval(fetchAll, 60_000);
    return () => clearInterval(t);
  }, [token, pathname]);

  // MY 아이콘 = 멘션 + DM + 친구 알림 + 고객문의 답변 통합
  const myCount = mentionCount + dmCount + friendNotiCount + supportCount;

  // DM 채팅 thread 풀스크린 모드 — 채팅 앱 표준 UX (BottomNav 숨김)
  if (pathname && /^\/mypage\/dm\/\d+/.test(pathname)) return null;

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-bg-secondary border-t border-border lg:bg-bg">
      <ul className="flex w-full lg:max-w-lg lg:mx-auto lg:bg-bg-secondary">
        {NAV_ITEMS.map((item) => {
          const href = item.href === "/mypage" && !isLoggedIn ? "/login" : item.href;
          const active = pathname === item.href;
          // 커뮤니티 = 멘션만 / MY = 멘션+DM+친구 통합
          let badge = 0;
          if (item.href === "/community") badge = mentionCount;
          else if (item.href === "/mypage") badge = myCount;

          return (
            <li key={item.href} className="flex-1">
              <Link
                href={href}
                className={clsx(
                  "flex flex-col items-center justify-center py-3 gap-0.5 transition-colors relative",
                  active ? "text-primary" : "text-text-sub hover:text-text"
                )}
              >
                {/* 활성 인디케이터 — 상단 막대 */}
                {active && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-b-full bg-primary" />
                )}
                <span className={clsx(
                  "text-xl leading-none relative transition-transform",
                  active && "scale-110"
                )}>
                  {item.icon}
                  {badge > 0 && (
                    <span className="absolute -top-1 -right-2 min-w-[1rem] h-4 px-1 rounded-full bg-red-500 text-bg text-[9px] font-bold flex items-center justify-center">
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </span>
                <span className={clsx(
                  "text-2xs font-medium transition-all",
                  active ? "text-primary font-bold" : ""
                )}>
                  {item.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
